import { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { apiRequests } from '../../shared/api/apiRequests.js';
import { dateToString } from '../../shared/utils/parseDate.js';
import { Pagination } from '../../features/Pagination/index.js';
import { useUrlPagination } from '../../shared/hooks/useUrlPagination.js';
import { PageBar } from '../../shared/ui/PageBar';
import { EntityCard } from '../../shared/ui/EntityCard';
import { EmptyState } from '../../shared/ui/EmptyState';
import { Plus, TicketPercent } from 'lucide-react';

const PAGE_SIZE = 5;

const normalize = (item) => ({
  ...item,
  priceFrom: item.priceFrom || { costs: [] },
  priceTo: item.priceTo || { costs: [] },
  percent: item.percent !== undefined ? item.percent : 0,
  discount: item.discount || { costs: [] },
});

const PromocodesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { page, offset, pageSize, setPage } = useUrlPagination(PAGE_SIZE);
  // Применённый поиск — в URL, в поле — черновик
  const query = searchParams.get('q') || '';
  const [data, setData] = useState({});
  const [searchValue, setSearchValue] = useState(query);
  const [reloadKey, setReloadKey] = useState(0);
  const location = useLocation();

  useEffect(() => setSearchValue(query), [query]);

  useEffect(() => {
    let cancelled = false;
    apiRequests.promocodes
      .get(pageSize, offset, query)
      .then((res) => {
        if (cancelled) return;
        if (res && res.items) setData({ ...res, items: res.items.map(normalize) });
        else setData({ items: [], totalCount: 0 });
      })
      .catch((error) => {
        if (cancelled) return;
        console.error('Ошибка при загрузке промокодов:', error);
        toast.error('Произошла ошибка при загрузке промокодов');
        setData({ items: [], totalCount: 0 });
      });
    return () => {
      cancelled = true;
    };
  }, [page, query, reloadKey]);

  const handleDelete = (id) => {
    if (!id) return;
    apiRequests.promocodes
      .delete(id)
      .then(() => {
        toast.success('Промокод успешно удален');
        if (data.items?.length === 1 && page > 1) setPage(page - 1);
        else setReloadKey((k) => k + 1);
      })
      .catch((error) => {
        console.error('Ошибка при удалении промокода:', error);
        toast.error('Произошла ошибка при удалении промокода');
      });
  };

  const handleSearch = () => {
    const search = searchValue.trim();
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (search) params.set('q', search);
      else params.delete('q');
      params.delete('page');
      return params;
    });
  };

  return (
    <>
      <PageBar
        searchValue={searchValue}
        onSearchChange={setSearchValue}
        onSearch={handleSearch}
        searchPlaceholder="Поиск промокодов..."
        createLink={`${location.pathname}/create`}
      />

      <div className="mb-3">
        {data.items?.length === 0 ? (
          query ? (
            <EmptyState
              icon={TicketPercent}
              variant="search"
              title="Промокод не найден"
              description="Такого кода нет. Проверьте регистр и символы — промокоды часто путают с похожими."
            />
          ) : (
            <EmptyState
              icon={TicketPercent}
              title="Промокодов пока нет"
              description="Заведите первый: процентную или фиксированную скидку, срок действия и минимальную сумму заказа."
              action={{ label: 'Создать промокод', to: `${location.pathname}/create`, icon: Plus }}
            />
          )
        ) : (
          data.items?.map((item) => (
            <EntityCard
              key={item.id}
              title={item.code}
              fields={[
                { label: 'Скидка', value: item.discriminator === 'PercentPromocode' ? `${item.percent || 0}%` : 'Фиксированная' },
                { label: 'Истекает', value: item.endDate ? dateToString(item.endDate) : 'Не указано' },
                { label: 'Создано', value: item.createdAt ? dateToString(item.createdAt) : 'Не указано' },
              ]}
              editLink={`${location.pathname}/${item.id}`}
              onDelete={() => handleDelete(item.id)}
            />
          ))
        )}
      </div>

      <Pagination
        page={page}
        total={data.totalCount || 0}
        pageSize={pageSize}
        onPageChange={setPage}
      />
    </>
  );
};

export default PromocodesPage;
